/**
 * useUsage Hook - Tracks LLM usage and limits
 *
 * WS-aware: listens to agent_complete events for instant optimistic deltas.
 * Falls back to 30s HTTP polling when WebSocket is disconnected.
 * Reconciles with server via HTTP every 5 minutes when WS is connected.
 */

import { axiom } from '../lib/axiom';
import { useReducer, useCallback, useEffect, useRef } from 'react';
import { api, ApiError } from '../lib/api';
import { useUserWebSocket } from '../contexts/UserWebSocketContext';
import type { UsageData } from '../types/api';
import type { StreamEvent } from '@pistachiorama/shared-types';

const POLL_INTERVAL_FALLBACK_MS = 30 * 1000;       // 30s when WS disconnected
const RECONCILE_INTERVAL_WS_MS = 5 * 60 * 1000;   // 5 min when WS connected

interface UseUsageReturn {
  data: UsageData | null;
  loading: boolean;
  error: string | null;
  isNearLimit: boolean;
  isOverLimit: boolean;
  refresh: () => Promise<void>;
}

// Atomic state to prevent race between refresh() and WS event handler.
// Uses fetch-start snapshot to preserve WS deltas that arrive during in-flight HTTP requests.
interface UsageState {
  serverData: UsageData | null;
  tokenDelta: number;
  requestDelta: number;
  // Snapshot of deltas at the time the last fetch started.
  // FETCH_SUCCESS subtracts these (now reflected in server data) while preserving
  // any new deltas that arrived during the in-flight request.
  fetchBaseTokenDelta: number;
  fetchBaseRequestDelta: number;
  loading: boolean;
  error: string | null;
}

type UsageAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; data: UsageData }
  | { type: 'FETCH_ERROR'; message: string }
  | { type: 'WS_DELTA'; tokens: number };

function usageReducer(state: UsageState, action: UsageAction): UsageState {
  switch (action.type) {
    case 'FETCH_START':
      // Snapshot current deltas — these will be subtracted on FETCH_SUCCESS
      // since the server response should reflect them (or will at next reconciliation).
      return { ...state, fetchBaseTokenDelta: state.tokenDelta, fetchBaseRequestDelta: state.requestDelta };
    case 'FETCH_SUCCESS':
      // Subtract only the pre-fetch deltas (now in server data). Any WS events
      // that arrived during the fetch are preserved as the remaining delta.
      return {
        ...state,
        serverData: action.data,
        tokenDelta: state.tokenDelta - state.fetchBaseTokenDelta,
        requestDelta: state.requestDelta - state.fetchBaseRequestDelta,
        fetchBaseTokenDelta: 0,
        fetchBaseRequestDelta: 0,
        error: null,
        loading: false,
      };
    case 'FETCH_ERROR':
      return { ...state, error: action.message, loading: false, fetchBaseTokenDelta: 0, fetchBaseRequestDelta: 0 };
    case 'WS_DELTA':
      return { ...state, tokenDelta: state.tokenDelta + action.tokens, requestDelta: state.requestDelta + 1 };
  }
}

export function useUsage({ pollInterval }: { pollInterval?: number } = {}): UseUsageReturn {
  const [state, dispatch] = useReducer(usageReducer, {
    serverData: null,
    tokenDelta: 0,
    requestDelta: 0,
    fetchBaseTokenDelta: 0,
    fetchBaseRequestDelta: 0,
    loading: true,
    error: null,
  });

  const { userWebSocket, status } = useUserWebSocket();
  const wsConnected = status === 'connected';
  // null = "not yet observed" (prevents spurious fetch on initial mount when already connected)
  const prevConnectedRef = useRef<boolean | null>(null);

  const refresh = useCallback(async () => {
    dispatch({ type: 'FETCH_START' });
    try {
      const response = await api.usage.stats();
      dispatch({ type: 'FETCH_SUCCESS', data: response });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load usage';
      dispatch({ type: 'FETCH_ERROR', message });
      axiom.error('Failed to load usage', { error: err instanceof Error ? err.message : String(err) });
    }
  }, []);

  // Initial load
  useEffect(() => {
    refresh();
  }, [refresh]);

  // WS reconnect: fetch immediately to sync server state and reset deltas
  // (only on disconnect→connect transition, not initial mount)
  useEffect(() => {
    const prev = prevConnectedRef.current;
    prevConnectedRef.current = wsConnected;
    if (wsConnected && prev === false) {
      refresh();
    }
  }, [wsConnected, refresh]);

  // Polling interval: fast fallback when WS down, slow reconciliation when WS up
  useEffect(() => {
    const interval = setInterval(refresh, pollInterval ?? (wsConnected ? RECONCILE_INTERVAL_WS_MS : POLL_INTERVAL_FALLBACK_MS));
    return () => clearInterval(interval);
  }, [refresh, wsConnected, pollInterval]);

  // WS event listener for instant optimistic usage updates
  useEffect(() => {
    if (!userWebSocket) return;

    const handler = (_streamId: string, event: StreamEvent) => {
      if (event.type !== 'agent_complete') return;
      dispatch({ type: 'WS_DELTA', tokens: event.usage.input + event.usage.output });
    };

    userWebSocket.on('streamEvent', handler);
    return () => {
      userWebSocket.off('streamEvent', handler);
    };
  }, [userWebSocket]);

  const { serverData, tokenDelta, requestDelta, loading, error } = state;

  // Merge server data with optimistic deltas from WS events.
  // When no delta is present, use server data verbatim (server computes percentages authoritatively).
  // When deltas are present, recalculate derived fields using server limits.
  const data: UsageData | null =
    serverData === null
      ? null
      : tokenDelta === 0 && requestDelta === 0
        ? serverData
        : {
            ...serverData,
            usage: {
              ...serverData.usage,
              daily_tokens: serverData.usage.daily_tokens + tokenDelta,
              monthly_tokens: serverData.usage.monthly_tokens + tokenDelta,
              daily_requests: serverData.usage.daily_requests + requestDelta,
            },
            percentages: {
              daily_tokens: serverData.limits.daily_tokens > 0
                ? ((serverData.usage.daily_tokens + tokenDelta) / serverData.limits.daily_tokens) * 100
                : 0,
              monthly_tokens: serverData.limits.monthly_tokens > 0
                ? ((serverData.usage.monthly_tokens + tokenDelta) / serverData.limits.monthly_tokens) * 100
                : 0,
              daily_requests: serverData.limits.daily_requests > 0
                ? ((serverData.usage.daily_requests + requestDelta) / serverData.limits.daily_requests) * 100
                : 0,
            },
            remaining: {
              daily_tokens: Math.max(0, serverData.limits.daily_tokens - (serverData.usage.daily_tokens + tokenDelta)),
              monthly_tokens: Math.max(0, serverData.limits.monthly_tokens - (serverData.usage.monthly_tokens + tokenDelta)),
              daily_requests: Math.max(0, serverData.limits.daily_requests - (serverData.usage.daily_requests + requestDelta)),
            },
          };

  const isNearLimit = data
    ? data.percentages.daily_tokens > 80 || data.percentages.monthly_tokens > 80
    : false;

  const isOverLimit = data
    ? data.percentages.daily_tokens >= 100 || data.percentages.monthly_tokens >= 100
    : false;

  return {
    data,
    loading,
    error,
    isNearLimit,
    isOverLimit,
    refresh,
  };
}
